import { LevelConfig, GameState, Bottle, ColorId } from "./types";
import { SeededRNG } from "./rng";
import { applyPour, getValidMoves } from "./rules";
import { deepCloneGameState } from "./undo";

export function generateSlotQueue(config: LevelConfig, rng: SeededRNG): ColorId[] {
  const { totalPacks, slotPolicy, colors } = config;
  
  if (slotPolicy.kind === "sequence") {
    return slotPolicy.sequence.slice(0, totalPacks);
  }
  
  const queue: ColorId[] = [];
  
  for (let i = 0; i < totalPacks; i++) {
    let color: ColorId;
    
    if (slotPolicy.kind === "biased") {
      const biasedProb = rng.nextFloat(0, 1);
      if (biasedProb < slotPolicy.biasStrength) {
        color = slotPolicy.biasColor;
      } else {
        const otherColors = colors.filter(c => c !== slotPolicy.biasColor);
        color = rng.pick(otherColors)!;
      }
    } else if (slotPolicy.kind === "delayed") {
      const delayThreshold = Math.floor(totalPacks * slotPolicy.delayUntilRatio);
      if (i < delayThreshold) {
        const nonDelayedColors = colors.filter(c => c !== slotPolicy.delayedColor);
        color = rng.pick(nonDelayedColors)!;
      } else {
        color = rng.pick(colors)!;
      }
    } else {
      color = rng.pick(colors)!;
    }
    
    queue.push(color);
  }
  
  return queue;
}

export function createSolvedState(config: LevelConfig, rng: SeededRNG): GameState {
  // Generate the colors we need to pack
  const slotQueue = generateSlotQueue(config, rng);
  
  // Build a list of all water units that need to be packed
  const allWaterUnits: ColorId[] = [];
  for (const color of slotQueue) {
    // Each packable bottle holds CAPACITY_L (5L) of same color
    for (let i = 0; i < config.capacity; i++) {
      allWaterUnits.push(color);
    }
  }
  
  const totalBottles = config.initialVisibleBottles + config.reserveBottleCount;
  const minNonEmptyBottles = Math.ceil(allWaterUnits.length / config.capacity);
  if (totalBottles < minNonEmptyBottles) {
    throw new Error("Not enough bottles to hold all water");
  }
  const maxEmptyAllowed = Math.max(0, totalBottles - minNonEmptyBottles);
  const targetEmptyBottles = Math.min(config.emptyBottleCount, maxEmptyAllowed);
  
  const allBottleContents: ColorId[][] = Array.from({ length: totalBottles }, () => []);
  const indices = rng.shuffle([...Array(totalBottles).keys()]);
  const emptySet = new Set(indices.slice(0, targetEmptyBottles));
  const nonEmptyIndices = indices.slice(targetEmptyBottles);
  
  const shuffledWater = rng.shuffle([...allWaterUnits]);
  let waterIndex = 0;
  
  for (const idx of nonEmptyIndices) {
    if (waterIndex >= shuffledWater.length) break;
    allBottleContents[idx].push(shuffledWater[waterIndex++]);
  }
  
  for (; waterIndex < shuffledWater.length; waterIndex++) {
    const color = shuffledWater[waterIndex];
    let placed = false;
    const candidateIndices = rng.shuffle([...nonEmptyIndices]);
    
    for (const i of candidateIndices) {
      if (emptySet.has(i)) continue;
      const content = allBottleContents[i];
      if (content.length >= config.capacity) continue;
      
      const sameColorCount = content.filter(c => c === color).length;
      const wouldBecomeFull = content.length === config.capacity - 1;
      const allSameAfterAdd = sameColorCount === content.length;
      const wouldBeFullMonoColor = wouldBecomeFull && allSameAfterAdd;
      const alreadyThreeSame = sameColorCount >= 3;
      
      if (!wouldBeFullMonoColor && !alreadyThreeSame) {
        content.push(color);
        placed = true;
        break;
      }
    }
    
    if (!placed) {
      for (const i of candidateIndices) {
        if (emptySet.has(i)) continue;
        const content = allBottleContents[i];
        if (content.length < config.capacity) {
          content.push(color);
          placed = true;
          break;
        }
      }
    }
    
    if (!placed) {
      throw new Error("Failed to distribute water units");
    }
  }
  
  const allBottles: Bottle[] = allBottleContents.map((content, i) => ({
    id: i,
    layers: content,
    sealed: false,
    visibility: config.capacity
  }));
  
  const initialSlots: Array<ColorId | null> = [];
  const remainingQueue = [...slotQueue];
  
  for (let i = 0; i < config.slotCount; i++) {
    if (remainingQueue.length > 0) {
      initialSlots.push(remainingQueue.shift()!);
    } else {
      initialSlots.push(null);
    }
  }
  
  return {
    level: config.level,
    seed: rng.getSeed(),
    bottles: allBottles,
    hiddenBottles: [],
    packing: {
      slots: initialSlots,
      queue: remainingQueue
    },
    score: 0,
    moves: [],
    phase: "idle",
    initialVisibleCount: allBottles.length
  };
}

function applyProgressiveSplit(state: GameState, config: LevelConfig, rng: SeededRNG): void {
  const totalExpected = config.initialVisibleBottles + config.reserveBottleCount;
  if (state.bottles.length !== totalExpected) {
    throw new Error("Bottle count mismatch during progressive split");
  }
  
  const emptyBottles = state.bottles.filter(b => b.layers.length === 0);
  const nonEmptyBottles = state.bottles.filter(b => b.layers.length > 0);
  const ordered = [
    ...rng.shuffle(emptyBottles),
    ...rng.shuffle(nonEmptyBottles)
  ];
  
  const visibleBottles = ordered.slice(0, config.initialVisibleBottles);
  const hiddenBottles = ordered.slice(config.initialVisibleBottles);
  
  let id = 0;
  for (const bottle of visibleBottles) {
    bottle.id = id++;
  }
  for (const bottle of hiddenBottles) {
    bottle.id = id++;
  }
  
  state.bottles = visibleBottles;
  state.hiddenBottles = hiddenBottles;
  state.initialVisibleCount = visibleBottles.length;
}

export function scrambleState(state: GameState, steps: number, rng: SeededRNG): void {
  // Simple random scramble - just pick random valid moves
  // This ensures solvability is preserved
  let consecutiveStuck = 0;

  for (let i = 0; i < steps; i++) {
    const validMoves = getValidMoves(state);

    if (validMoves.length === 0) {
      consecutiveStuck++;
      if (consecutiveStuck > 10) break;
      continue;
    }

    consecutiveStuck = 0;
    const move = rng.pick(validMoves)!;
    applyPour(state, move, false);
  }

  state.moves = [];
}

export function fixFullMonochromaticBottles(state: GameState, config: LevelConfig, rng: SeededRNG): void {
  let fixedCount = 0;
  for (const bottle of state.bottles) {
    if (bottle.layers.length === config.capacity && bottle.layers.length > 0) {
      const firstColor = bottle.layers[0];
      const isMonochromatic = bottle.layers.every(c => c === firstColor);

      if (isMonochromatic) {
        const bottleIndex = state.bottles.indexOf(bottle);
        const candidateIndices = rng.shuffle(
          state.bottles
            .map((_b, index) => index)
            .filter(index => index !== bottleIndex)
        );
        let swapped = false;

        for (const candidateIndex of candidateIndices) {
          const candidate = state.bottles[candidateIndex];
          if (candidate.layers.length === 0) continue;

          const layerIndices = rng.shuffle(
            [...Array(candidate.layers.length).keys()]
          );
          for (const layerIndex of layerIndices) {
            const candidateColor = candidate.layers[layerIndex];
            if (candidateColor === firstColor) continue;

            const wouldBecomeMono = candidate.layers.every((color, idx) =>
              (idx === layerIndex ? firstColor : color) === firstColor
            );
            if (wouldBecomeMono) continue;

            candidate.layers[layerIndex] = firstColor;
            bottle.layers[bottle.layers.length - 1] = candidateColor;
            swapped = true;
            fixedCount++;
            break;
          }

          if (swapped) break;
        }

        if (!swapped) {
          console.warn("Could not fix monochromatic bottle without changing water counts");
        }
      }
    }
  }

  if (fixedCount > 0) {
    console.log(`Fixed ${fixedCount} full monochromatic bottle(s)`);
  }
}

export function applyVisibilityMask(state: GameState, config: LevelConfig, rng: SeededRNG): void {
  // Only apply to visible bottles (hiddenBottles are already hidden by design)
  const hiddenCount = Math.floor(state.bottles.length * config.hiddenBottleRatio);
  const shuffledBottles = rng.shuffle([...state.bottles]);
  
  for (let i = 0; i < hiddenCount; i++) {
    shuffledBottles[i].visibility = config.hiddenVisibleLayers;
  }
}

export function generateLevel(config: LevelConfig, seed?: number): GameState {
  const rng = new SeededRNG(seed ?? config.seed ?? Date.now());
  
  let attempts = 0;
  const maxAttempts = config.maxReroll;
  
  while (attempts < maxAttempts) {
    attempts++;
    const currentSeed = (seed ?? config.seed ?? Date.now()) + attempts;
    rng.reset(currentSeed);
    
    const state = createSolvedState(config, rng);
    
    // Check initial state has valid moves
    if (state.bottles.length === 0) {
      console.log(`Attempt ${attempts}: No visible bottles, retrying...`);
      continue;
    }
    
    // Scramble with smaller steps first
    const scrambleSteps = Math.min(config.scrambleSteps, 30 + attempts * 5);
    scrambleState(state, scrambleSteps, rng);

    // Fix any full monochromatic bottles created during scramble
    fixFullMonochromaticBottles(state, config, rng);

    applyProgressiveSplit(state, config, rng);
    applyVisibilityMask(state, config, rng);
    
    if (!config.solvability.validate) {
      console.log(`Generated level ${config.level} (validation skipped)`);
      return state;
    }
    
    // Check solvability with progressive unlock support
    const solvabilityConfig = {
      ...config,
      solvability: {
        ...config.solvability,
        maxNodes: config.solvability.maxNodes + attempts * 1000,
        timeBudgetMs: config.solvability.timeBudgetMs + attempts * 50
      }
    };
    
    if (isSolvableWithProgressiveUnlock(state, solvabilityConfig)) {
      console.log(`Generated solvable level ${config.level} after ${attempts} attempts`);
      return state;
    }
    
    console.log(`Attempt ${attempts} failed solvability check, retrying...`);
  }
  
  // Last resort
  console.warn(`Could not verify solvability after ${maxAttempts} attempts, returning best effort`);
  rng.reset((seed ?? config.seed ?? Date.now()) + maxAttempts);
  const state = createSolvedState(config, rng);
  scrambleState(state, 20, rng);
  fixFullMonochromaticBottles(state, config, rng);
  applyProgressiveSplit(state, config, rng);
  applyVisibilityMask(state, config, rng);
  return state;
}

export function isSolvable(state: GameState, config: LevelConfig): boolean {
  // Quick check
  const initialMoves = getValidMoves(state);
  if (initialMoves.length === 0) {
    return state.bottles.every(b => b.layers.length === 0);
  }
  
  const cloned = deepCloneGameState(state);
  const visited = new Set<string>();
  const queue: GameState[] = [cloned];
  let nodesVisited = 0;
  const startTime = Date.now();
  
  while (queue.length > 0) {
    if (nodesVisited >= config.solvability.maxNodes) return true;
    if (Date.now() - startTime > config.solvability.timeBudgetMs) return true;
    
    const current = queue.shift()!;
    nodesVisited++;
    
    const hash = current.bottles.map(b => 
      `${b.id}:${b.layers.join(',')}:${b.sealed ? 1 : 0}`
    ).join('|') + `|slots:${current.packing.slots.join(',')}`;
    
    if (visited.has(hash)) continue;
    visited.add(hash);
    
    if (current.bottles.every(b => b.layers.length === 0)) {
      return true;
    }
    
    if (current.moves.length > config.solvability.maxDepth) continue;
    
    const moves = getValidMoves(current);
    for (const move of moves) {
      const nextState = deepCloneGameState(current);
      applyPour(nextState, move, true);
      queue.push(nextState);
    }
  }
  
  return false;
}

export function isSolvableWithProgressiveUnlock(state: GameState, config: LevelConfig): boolean {
  // First check: current visible state should be solvable OR have valid moves
  const visibleMoves = getValidMoves(state);
  if (visibleMoves.length === 0 && !state.bottles.every(b => b.layers.length === 0)) {
    // No moves and not won = likely unsolvable
    // But check if hidden bottles can help
    if (state.hiddenBottles.length === 0) {
      return false;
    }
  }
  
  // Simulate progressive unlock during solving
  const cloned = deepCloneGameState(state);
  const visited = new Set<string>();
  const queue: GameState[] = [cloned];
  let nodesVisited = 0;
  const startTime = Date.now();
  
  while (queue.length > 0) {
    if (nodesVisited >= config.solvability.maxNodes) return true;
    if (Date.now() - startTime > config.solvability.timeBudgetMs) return true;
    
    const current = queue.shift()!;
    nodesVisited++;
    
    // Create hash including hidden bottles
    const visibleHash = current.bottles.map(b => 
      `${b.id}:${b.layers.join(',')}:${b.sealed ? 1 : 0}`
    ).join('|') + `|slots:${current.packing.slots.join(',')}`;
    const hiddenHash = current.hiddenBottles.map(b =>
      `${b.id}:${b.layers.join(',')}`
    ).join('|');
    const hash = visibleHash + '|hidden:' + hiddenHash;
    
    if (visited.has(hash)) continue;
    visited.add(hash);
    
    // Check win: all bottles empty (both visible and hidden)
    const allBottlesEmpty = current.bottles.every(b => b.layers.length === 0) &&
                           current.hiddenBottles.every(b => b.layers.length === 0);
    if (allBottlesEmpty) {
      return true;
    }
    
    if (current.moves.length > config.solvability.maxDepth) continue;
    
    // Simulate progressive unlock: when a bottle is packed away,
    // bring in a hidden bottle if available
    const moves = getValidMoves(current);
    
    for (const move of moves) {
      const nextState = deepCloneGameState(current);
      applyPour(nextState, move, true);
      
      // Simulate progressive unlock - happens automatically during applyPour
      // (the pack logic in rules should handle this)
      
      queue.push(nextState);
    }
  }
  
  return false;
}

export function generateLevelAsync(config: LevelConfig, seed?: number): Promise<GameState> {
  return new Promise((resolve, reject) => {
    try {
      const state = generateLevel(config, seed);
      resolve(state);
    } catch (error) {
      reject(error);
    }
  });
}
